/**
 * useAgentChat — TV-P7-11 (2026-06-02)
 *
 * Drives the AI patient agent chat surface in:
 *   - /ai-agent/:encounterId (AIPatientAgent.jsx, full page)
 *   - /waiting-room AIPanel (PatientWaitingRoom.jsx, sidebar)
 *
 * Two responsibilities:
 *   1. Subscribe to Firestore `ai-agent/{encounterId}/messages/*`
 *      and expose the live chat history (ordered by createdAt).
 *   2. Provide a `sendMessage(text)` that calls the `agentRespond`
 *      Cloud Function (TV-P7-10). The function persists both the
 *      user turn AND the assistant reply server-side, so we don't
 *      optimistic-write — we let the snapshot listener pick up the
 *      new docs as they land.
 *
 * The hook is pure UI plumbing. Persona routing (patient vs
 * provider system prompt) happens server-side in agentRespond
 * based on the KC `fhirUser` claim — the caller doesn't tell it
 * which to use.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, firebaseFunctions } from './config';
import { useSessionStore } from '@/stores/sessionStore';
import { useAuth } from '@/auth/AuthProvider';
// STAB-06 — gate the Firestore subscribe on Firebase Auth being signed
// in. Without this, useAgentChat fires onSnapshot the moment encounterId
// is set, but Firebase Auth lands ~100-300ms later (after the
// exchangeKeycloakToken round-trip). The first attempt hits
// request.auth=null in the Firestore rule, gets PERMISSION_DENIED, and
// the listener stays in error state (Firestore SDK doesn't auto-retry
// permission-denied). Same fix shape as useTenantQueue.
import { useFirebaseAuthSync } from './hooks';

const agentRespondCallable = httpsCallable(firebaseFunctions, 'agentRespond');

export function useAgentChat(encounterId) {
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);

  // Pull tenantId + patientId from session store so the caller
  // doesn't have to plumb them through every component that uses
  // the hook. Set by AuthProvider on launch (TV-P0-04).
  const tenantId = useSessionStore((s) => s.tenantId);
  const patientId = useSessionStore(
    (s) => s.currentPatient || s.smartContext?.patientId || null,
  );

  // KC bearer for the Cloud Function call — agentRespond verifies
  // it before any LLM work happens.
  const { token: kcToken } = useAuth();

  // STAB-06 — Firebase Auth readiness gate. Subscribing before this
  // is true produces a one-shot permission-denied because the rule
  // sees request.auth=null and Firestore won't recover.
  const { ready: fbReady } = useFirebaseAuthSync();

  // ── Subscribe to message history ────────────────────────────
  useEffect(() => {
    if (!encounterId) {
      setMessages([]);
      return undefined;
    }
    // Don't subscribe until Firebase Auth has signed in. Once `fbReady`
    // flips true, the effect re-runs and the listener attaches with a
    // populated request.auth that the Firestore rule can match against.
    if (!fbReady) {
      return undefined;
    }
    setError(null);

    const ref = collection(firestore, 'ai-agent', encounterId, 'messages');
    const q = query(ref, orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setMessages(
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          })),
        );
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.warn('[useAgentChat] subscribe error:', err.message);
        setError(err);
      },
    );
    return () => unsub();
  }, [encounterId, fbReady]);

  // ── Send a new message ──────────────────────────────────────
  const sendMessage = useCallback(
    async (text) => {
      if (!text?.trim()) return null;
      if (!encounterId || !tenantId || !patientId) {
        const err = new Error(
          'Missing encounterId / tenantId / patientId — agent chat needs all three.',
        );
        setError(err);
        return null;
      }
      if (!kcToken) {
        const err = new Error('Not signed in — cannot reach the AI agent.');
        setError(err);
        return null;
      }
      setSending(true);
      setError(null);
      try {
        const res = await agentRespondCallable({
          encounterId,
          tenantId,
          patientId,
          message: text.trim(),
          kcToken,
        });
        return res.data ?? null;
      } catch (err) {
        setError(err);
        return null;
      } finally {
        setSending(false);
      }
    },
    [encounterId, tenantId, patientId, kcToken],
  );

  return { messages, sendMessage, sending, error };
}
